import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {
  CheckOutStatus,
  CheckOutStatusInfo,
  SAMPLE_RESTRICTION_ACCESS_LABELS,
  SAMPLE_RESTRICTION_STATUS_LABELS,
  SampleRestrictionAccess,
  SampleRestrictionStatus,
  SampleStatusItem,
} from 'constants/SampleStatusConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
// @ts-expect-error
import {isTeam} from 'orgs/OrganizationUtil';
import {PLATE_SCHEMA_TYPE} from 'samples/storables/StorableSchemaConstants';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';
import {capitalize, constantCaseToWords} from 'util/NameUtil';
import {MakeFieldsRequired} from 'util/Types';

import {
  IsContainerFixedPlateWellQuery,
  IsContainerFixedPlateWellQueryVariables,
} from './__generated/IsContainerFixedPlateWellQuery';

type ParentOrInaccessible = NonNullable<
  ExtractByTypename<IsContainerFixedPlateWellQuery['containers'][number], 'ContainerNode'>['parentStorable']
>;

type SchemaInfo = ExtractByTypename<ParentOrInaccessible, 'BoxNode'>['storableSchema'];

type PlateSchemaInfo = ExtractByTypename<SchemaInfo, 'PlateSchemaNode'>;

type InaccessibleParentStorable = ExtractByTypename<
  ExtractByTypename<IsContainerFixedPlateWellQuery['containers'][number], 'ContainerNode'>['parentStorable'],
  'InaccessibleInfoNode'
>;

export const IS_CONTAINER_FIXED_PLATE_WELL_QUERY = gql`
  query IsContainerFixedPlateWellQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        parentStorable {
          ... on StorableInterface {
            id
            storableSchema {
              ... on PlateSchemaNode {
                id
                plateSchemaType
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function isPlate(schemaInfo: SchemaInfo): schemaInfo is PlateSchemaInfo {
  return schemaInfo.hasOwnProperty('plateSchemaType');
}

function isParentInaccessible(
  parentStorable: ParentOrInaccessible
): parentStorable is InaccessibleParentStorable {
  return parentStorable.__typename === 'InaccessibleInfoNode';
}

export function isFixedPlateWell(container: {parentStorable?: ParentOrInaccessible | null}): boolean {
  const parentStorable = container.parentStorable;
  if (
    parentStorable == null ||
    isParentInaccessible(parentStorable) ||
    !isPlate(parentStorable.storableSchema)
  ) {
    return false;
  }
  return parentStorable.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE;
}

export function doesItemSupportSampleStatus(item: SampleStatusItem): boolean {
  return APIUtil.isContainer(item.id) && !isFixedPlateWell(item);
}

async function queryForFixedPlateWellhood(containerId: string): Promise<boolean> {
  const {containers} = await doQuerySafe<
    IsContainerFixedPlateWellQuery,
    IsContainerFixedPlateWellQueryVariables
  >({query: IS_CONTAINER_FIXED_PLATE_WELL_QUERY}, {variables: {containerIds: [containerId]}});
  const container = containers[0];
  assert(!isInaccessible(container), 'Container must be accessible.');
  return isFixedPlateWell(container);
}

export async function queryForItemSampleSupport(itemId: string): Promise<boolean> {
  return APIUtil.isContainer(itemId) && !(await queryForFixedPlateWellhood(itemId));
}

export function checkOutStatusInfoToStatusText(statusInfo: CheckOutStatusInfo): string {
  return capitalize(constantCaseToWords(statusInfo.status));
}

interface CICOStatusDisplayOptions {
  hideAssignee: boolean;
  includeTimeAndComment?: boolean;
}

export function getCICOStatusText(
  item: SampleStatusItem,
  itemSupportsSampleStatus: boolean,
  {hideAssignee, includeTimeAndComment = false}: CICOStatusDisplayOptions
): string {
  if (!itemSupportsSampleStatus) {
    return 'N/A';
  }

  const statusInfo = item.checkOutStatusInfo;
  if (statusInfo == null) {
    return 'Available'; // For containers that don't have CICOStatusInfo back-filled
  }
  return getCICOWarningText(statusInfo, {hideAssignee, includeTimeAndComment});
}

export function getCICOWarningText(
  statusInfo: CheckOutStatusInfo,
  {hideAssignee, includeTimeAndComment}: CICOStatusDisplayOptions
): string {
  const actionText = checkOutStatusInfoToStatusText(statusInfo);
  let CICOStatusText;
  if (hideAssignee || statusInfo.assignee == null) {
    CICOStatusText = actionText;
  } else if (statusInfo.isAssignedToCurrentUser && !isTeam(statusInfo.assignee.id)) {
    CICOStatusText = `${actionText} for you`;
  } else {
    CICOStatusText = `${actionText} for ${statusInfo.assignee.name}`;
  }
  return includeTimeAndComment
    ? `${CICOStatusText} on ${readableTime(statusInfo.createdAt)}${
        statusInfo.comment ? `\n"${statusInfo.comment}"` : ''
      }`
    : CICOStatusText;
}

export function getRestrictionAccessHeaderText(): string {
  return hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) ? 'Restriction access' : 'Authorization';
}

export function getRestrictionAccessDisplayText(item: SampleStatusItem): string {
  if (!isItemRestricted(item)) {
    return SAMPLE_RESTRICTION_ACCESS_LABELS[SampleRestrictionAccess.NOT_APPLICABLE];
  }
  const authorizedText = hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
    ? SAMPLE_RESTRICTION_ACCESS_LABELS[SampleRestrictionAccess.ACCESS_GRANTED]
    : 'Authorized';
  const unauthorizedText = hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
    ? SAMPLE_RESTRICTION_ACCESS_LABELS[SampleRestrictionAccess.NO_ACCESS]
    : 'Unauthorized';
  return item.sampleRoles?.isCurrentUserRestrictedSampleUser ? authorizedText : unauthorizedText;
}

export function isItemAvailable(
  item?: SampleStatusItem | null
): item is MakeFieldsRequired<SampleStatusItem, 'checkOutStatusInfo'> {
  return (
    item == null ||
    item.checkOutStatusInfo == null ||
    item.checkOutStatusInfo.status === CheckOutStatus.AVAILABLE
  );
}

export function isItemAvailableToCurrentUser(
  item?: SampleStatusItem | null
): item is MakeFieldsRequired<SampleStatusItem, 'checkOutStatusInfo'> {
  return isItemAvailable(item) || Boolean(item?.checkOutStatusInfo?.isAssignedToCurrentUser);
}

export function isItemRestricted(item: SampleStatusItem): boolean {
  return hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
    ? item.restrictionStatus === SampleRestrictionStatus.RESTRICTED
    : hasSampleOwners(item);
}

export function getSampleOwnersText(item: SampleStatusItem, itemSupportsSampleStatus: boolean): string {
  if ((item.sampleRoles?.sampleOwners?.length ?? 0) === 0) {
    return itemSupportsSampleStatus ? 'None' : 'N/A';
  }
  const sampleOwners = item.sampleRoles!.sampleOwners!.map((party) => party.name);
  return sampleOwners.join(', ');
}

export function getRestrictionStatusFromCaseInsensitiveLabel(
  statusLabel: string
): SampleRestrictionStatus | null {
  const possibleStatus = statusLabel.toUpperCase().trim() as SampleRestrictionStatus;
  if (Object.values(SampleRestrictionStatus).includes(possibleStatus)) {
    return possibleStatus;
  } else if (statusLabel.toUpperCase().trim() === 'N/A') {
    return SampleRestrictionStatus.NOT_APPLICABLE;
  }
  return null;
}

export function getValidRestrictionStatusesForContainer(): Array<SampleRestrictionStatus> {
  return Object.values(SampleRestrictionStatus).filter(
    (status) => status !== SampleRestrictionStatus.NOT_APPLICABLE
  );
}

export function getRestrictionStatusStringFromEnum(restrictionStatus: SampleRestrictionStatus): string {
  return SAMPLE_RESTRICTION_STATUS_LABELS[restrictionStatus];
}

export function getRestrictionStatusText(item: SampleStatusItem, itemSupportsSampleStatus: boolean): string {
  if (!itemSupportsSampleStatus) {
    return 'N/A';
  }
  return getRestrictionStatusStringFromEnum(item.restrictionStatus ?? SampleRestrictionStatus.UNRESTRICTED);
}

export function hasSampleOwners(item?: SampleStatusItem | null): boolean {
  return (item?.sampleRoles?.sampleOwners?.length ?? 0) > 0;
}

export function doesCurrentUserHaveAccessForItem(item?: SampleStatusItem | null): boolean {
  return (
    item == null || !isItemRestricted(item) || Boolean(item?.sampleRoles?.isCurrentUserRestrictedSampleUser)
  );
}

export function doesItemHaveBlockingStatusWarning(item: SampleStatusItem): boolean {
  return !doesCurrentUserHaveAccessForItem(item) && hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3);
}

export function getBlockingStatusWarningTooltip(item: SampleStatusItem): string | null {
  return doesItemHaveBlockingStatusWarning(item)
    ? 'You must be a restricted sample user to edit this item'
    : null;
}

export function hasRestrictedSampleUsers(item: SampleStatusItem): boolean {
  return (item.sampleRoles?.restrictedSampleUsers?.length ?? 0) > 0;
}

export function canCurrentUserEditRestrictionStatus(item: SampleStatusItem): boolean {
  return item.restrictionStatus === SampleRestrictionStatus.RESTRICTED
    ? (item.permissions?.canUnrestrictSamples ?? false)
    : (item.permissions?.canRestrictSamples ?? false);
}

export function canCurrentUserEditAnyRestrictionStatus(item: SampleStatusItem): boolean {
  return (item.permissions?.canRestrictSamples ?? false) || (item.permissions?.canUnrestrictSamples ?? false);
}

export function canCurrentUserSetSpecifiedRestrictionStatus(
  item: SampleStatusItem,
  specifiedStatus: SampleRestrictionStatus
): boolean {
  return specifiedStatus === SampleRestrictionStatus.RESTRICTED
    ? canCurrentUserRestrictSample(item)
    : canCurrentUserUnrestrictSample(item);
}

export function canCurrentUserUnrestrictSample(item: SampleStatusItem): boolean {
  // If any RSUs are present, they will be cleared and thus the user must have canEditRestrictedSampleUsers too
  const hasAnyRSUPerms =
    (item.sampleRoles?.restrictedSampleUsers ?? []).length === 0 ||
    (item.permissions?.canEditRestrictedSampleUsers ?? false);
  return item.restrictionStatus === SampleRestrictionStatus.RESTRICTED
    ? (item.permissions?.canUnrestrictSamples ?? false) && hasAnyRSUPerms
    : true;
}

export function canCurrentUserRestrictSample(item: SampleStatusItem): boolean {
  return item.restrictionStatus === SampleRestrictionStatus.UNRESTRICTED
    ? (item.permissions?.canRestrictSamples ?? false)
    : true;
}

export function canCurrentUserEditSampleOwners(item: SampleStatusItem): boolean {
  return (
    item.permissions?.admin ||
    (item.sampleRoles?.sampleOwners != null && item.sampleRoles.sampleOwners.length === 0)
  );
}

export function canCurrentUserEditRestrictedSampleUsers(
  item: SampleStatusItem,
  allowSettingOnUnrestrictedSample: boolean = true // Allows restricting sample and adding RSUs in a single action
): boolean {
  const isSampleOwner = Boolean(item.sampleRoles?.isCurrentUserSampleOwner);
  if (!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
    return isSampleOwner; // Maintain existing behavior when WIP flag is off
  } else if (item.restrictionStatus === SampleRestrictionStatus.RESTRICTED) {
    return isSampleOwner || (item.permissions?.canEditRestrictedSampleUsers ?? false);
  } else {
    return allowSettingOnUnrestrictedSample && (item.permissions?.canRestrictSamples ?? false);
  }
}

export function canCurrentUserEditAnySampleControls(item: SampleStatusItem): boolean {
  return (
    canCurrentUserEditRestrictionStatus(item) ||
    canCurrentUserEditSampleOwners(item) ||
    canCurrentUserEditRestrictedSampleUsers(item)
  );
}

interface TooltipDisplayOptions {
  includeAvailability: boolean;
  includeAuthorization: boolean;
  includeExpiration?: boolean;
  hideAssignee?: boolean;
}

export function getSampleStatusTooltip(
  item: SampleStatusItem,
  {includeAvailability, includeAuthorization, includeExpiration, hideAssignee = false}: TooltipDisplayOptions
): string | null {
  const unauthorizedTooltip =
    includeAuthorization && !doesCurrentUserHaveAccessForItem(item)
      ? getRestrictionAccessDisplayText(item)
      : undefined;
  const unavailableTooltip =
    includeAvailability && !isItemAvailable(item)
      ? getCICOWarningText(item.checkOutStatusInfo!, {
          hideAssignee,
          includeTimeAndComment: true,
        })
      : undefined;
  const expirationTooltip = includeExpiration && item.expirationInfo?.isExpired ? 'Expired' : undefined;
  const combinedTooltip = [unauthorizedTooltip, expirationTooltip, unavailableTooltip]
    .filter((tooltip) => tooltip != null)
    .join('\n');
  return combinedTooltip === '' ? null : combinedTooltip;
}

export function getEditSampleRestrictionStatusDisabledMessage(
  currentRestrictionStatus: SampleRestrictionStatus,
  isForSingleSample?: boolean
): string {
  return `Sorry, you have insufficient permissions to ${
    currentRestrictionStatus === SampleRestrictionStatus.RESTRICTED ? 'unrestrict' : 'restrict'
  } ${isForSingleSample ? 'this sample' : 'one or more of the selected samples'}.`;
}
